import { Component, Host, Prop, h, Element, State } from '@stencil/core';
import { htmlSanitizer } from '../../utils/html-sanitizer';
import { BackgroundColor } from './background-color.enum';

/**
 * @shortDescription A component to display quote content with attribution
 * @slot - The quote text
 * @csspart container - The main container of the quote
 */
@Component({
  tag: 'vs-quote',
  shadow: false,
})
export class VsQuote {
  /**
   * The background color of the quote
   * @type {'vs-tertiary'|'vs-secondary'|'vs-primary'|'vs-primary-dark'|'vs-lightBlue'}
   */
  @Prop() backgroundColor: BackgroundColor = BackgroundColor.VsTertiary;

  /**
   * The quote text to be displayed
   */
  @Prop() quoteText: string;

  /**
   * Style for the quote text (h2, h3, h4, display-3)
   */
  @Prop() quoteTextStyle: 'h2' | 'h3' | 'h4' | 'display-3' = 'display-3';

  /**
   * Whether to show the quote symbol
   */
  @Prop() showQuoteMark: string;

  /**
   * Path to the portrait image
   */
  @Prop() attrImagePath: string;

  /**
   * Alt text for the portrait image
   */
  @Prop() attrImageAltText: string;

  /**
   * Path to the logo image
   */
  @Prop() attrLogoImagePath: string;

  /**
   * Alt text for the logo image
   */
  @Prop() attrLogoImageAltText: string;

  /**
   * Attribution text for the quote
   */
  @Prop() attrTitle: string;

  /**
   * Whether to remove the default 30px bottom margin
   */
  @Prop() removeBottomMargin: boolean = false;

  /**
   * Link properties for CTA
   * Can be a JSON string or a CtaProps object
   */
  @Prop() cta: string | CtaProps;

  /**
   * Alternative property name for CTA JSON
   * Added for backward compatibility with existing implementations
   */
  @Prop() ctajson: string;

  @Element() el: HTMLElement;

  // Track animation states
  @State() iconActive: boolean = false;
  @State() quoteActive: boolean = false;
  @State() contentActive: boolean = false;

  // Observer reference
  private observer: IntersectionObserver;

  /**
   * Returns a CSS class string based on the background color
   */

  /**
   * Determines if the text should be displayed in alternative color based on background
   */
  private isDarkBackground(): boolean {
    return this.backgroundColor === BackgroundColor.VsPrimary || this.backgroundColor === BackgroundColor.VsPrimaryDark;
  }

  /**
   * Returns the quote text with appropriate styling
   */
  private renderQuoteText() {
    // Add text color class based on background
    const textColorClass = this.isDarkBackground() ? 'vs-alt' : '';
    // Add top margin when quote mark is not shown
    const noQuoteMarkClass = !this.showQuoteMark ? 'vs-mt-2' : '';

    // Always use p tag with vs-display-3 class for consistent styling
    if (this.quoteTextStyle === 'display-3') {
      return (
        <div class="vs-row">
          <div class={`vs-blockquote vs-alt vs-col vs-px-md-1 vs-px-xl-0 vs-pt-lg-1 vs-mb-0 ${noQuoteMarkClass}`}>
            <p class={`vs-slideup vs-offset-md-1 vs-col-md-11 vs-px-0 vs-px-md-1 vs-px-lg-0 vs-px-xl-1 vs-mb-2 vs-mb-md-1 vs-pb-md-1 vs-mb-xl-2 vs-pb-xl-2 vs-display-3 ${textColorClass} ${this.quoteActive ? 'vs-active' : ''}`} innerHTML={htmlSanitizer(this.quoteText)}></p>
          </div>
        </div>
      );
    } else {
      // Use the specified heading tag but with the same class structure as the p tag
      const HeadingTag = this.quoteTextStyle;
      return (
        <div class="vs-row">
          <div class={`vs-blockquote vs-alt vs-col vs-px-md-1 vs-px-xl-0 vs-pt-lg-1 vs-mb-0 ${noQuoteMarkClass}`}>
            <HeadingTag class={`vs-slideup vs-offset-md-1 vs-col-md-11 vs-px-0 vs-px-md-1 vs-px-lg-0 vs-px-xl-1 vs-mb-2 vs-mb-md-1 vs-pb-md-1 vs-mb-xl-2 vs-pb-xl-2 vs-display-3 ${textColorClass} ${this.quoteActive ? 'vs-active' : ''}`} innerHTML={htmlSanitizer(this.quoteText)}></HeadingTag>
          </div>
        </div>
      );
    }
  }

  /**
   * Renders the quote icon
   */
  private renderQuoteIcon() {
    const iconColorClass = this.backgroundColor === BackgroundColor.VsLightBlue
      ? 'vs-color-ocean'
      : 'vs-secondary-text';

    // Handle both boolean true and string "true" cases
    if (this.showQuoteMark === "true") {
      return (
        <div class="vs-row">
          <div class="vs-blockquote vs-alt vs-col vs-px-md-1 vs-px-xl-0 vs-mb-2">
            <span class={`vs-blockquote-icon ${iconColorClass} vs-slide vs-left ${this.iconActive ? 'vs-active' : ''}`}></span>
          </div>
        </div>
      );
    }
    return null;
  }

  /**
   * Renders the attribution section of the quote
   */
  private renderAttribution() {
    // Add text color class based on background
    const textColorClass = this.isDarkBackground() ? 'vs-alt' : '';

    return (
      <div class="vs-row">
        <div class={`vs-offset-md-1 vs-offset-xl-2 vs-col-md-11 vs-col-xl-10 vs-quote-content vs-slide vs-right ${this.contentActive ? 'vs-active' : ''}`}>
          {this.attrImagePath && (
            <div class="vs-row">
              <div class="vs-col vs-pb-1">
                <img alt={this.attrImageAltText || ''}
                     class="vs-quote-portrait-img vs-img-circle"
                     src={this.attrImagePath}
                     data-src={this.attrImagePath}
                     loading="lazy" />
              </div>
            </div>
          )}

          {this.attrLogoImagePath && (
            <div class="vs-row">
              <div class="vs-col vs-mb-1">
                <img alt={this.attrLogoImageAltText || ''}
                     class="vs-quote-logo-img"
                     src={this.attrLogoImagePath}
                     data-src={this.attrLogoImagePath}
                     loading="lazy" />
              </div>
            </div>
          )}

          {this.attrTitle && (
            <div class="vs-row">
              <div class="vs-pb-2 vs-pb-md-1 vs-mb-md-1 vs-col">
                <cite class={`vs-text vs-mb-0 ${textColorClass}`} innerHTML={`— ${htmlSanitizer(this.attrTitle)}`}></cite>
              </div>
            </div>
          )}

          {this.renderCTA()}
        </div>
      </div>
    );
  }

  /**
   * Renders the CTA for the quote
   */
  private renderCTA() {
    // Check if CTA exists and has at least a title to display
    if (this.cta &&
        typeof this.cta === 'object' &&
        (this.cta as CtaProps).ctaTitle &&
        (this.cta as CtaProps).ctaTitle.trim() !== '') {

      // Create a modified version of the CTA configuration
      const modifiedCta = {...this.cta as CtaProps};

      // Set to link style but ensure we keep the right icon configuration
      modifiedCta.ctaStyle = 'link';

      // Check if disableCta is set to any value that would indicate the CTA should be disabled
      if (modifiedCta.disableCta === 'yes' || modifiedCta.disableCta === 'true' || modifiedCta.disableCta === '1') {
        return null;
      }

      return (
        <div class="vs-row">
          <div class="vs-col vs-mb-0">
            <vs-link
              link-aria-label={modifiedCta.ctaLinkAriaLabel}
              button-color={modifiedCta.ctaButtonColor}
              link-conversion={modifiedCta.ctaConversionType}
              link-type={modifiedCta.ctaLinkType}
              link-style={modifiedCta.ctaStyle}
              has-dark-bg={this.isDarkBackground() ? 'yes' : 'no'}
              link-target={modifiedCta.ctaLinkTarget}
              link-text={modifiedCta.ctaTitle}
              img-path={modifiedCta.ctaVideoImagePath}
              video-title={modifiedCta.ctaVideoTitle}
              video-url={modifiedCta.ctaVideoUrl}
              link-val={modifiedCta.ctaLinkVal || modifiedCta.ctaLink}
              anchor-label={modifiedCta.ctaAnchorLabel}
              is-no-follow={modifiedCta.ctaNoFollow}
            ></vs-link>
          </div>
        </div>
      );
    }
    return null;
  }

  // Add this lifecycle method to parse the CTA JSON
  componentWillLoad() {
    // If ctajson is provided but cta is not, use ctajson value
    if (this.ctajson && !this.cta) {
      this.cta = this.ctajson;
    }

    // Parse the CTA if it's a string
    if (typeof this.cta === 'string' && this.cta.trim() !== '') {
      try {
        // Try to remove any potential trailing truncated text
        let ctaString = this.cta;

        // Find the last complete closing curly brace
        const lastValidIndex = ctaString.lastIndexOf('}');
        if (lastValidIndex > 0) {
          ctaString = ctaString.substring(0, lastValidIndex + 1);
        }

        // Improved regex to handle all property names with colons
        // This handles cases like ":type":"value" anywhere in the JSON
        ctaString = ctaString.replace(/"(:[^"]+)":/g, '"_$1":');

        const parsedCta = JSON.parse(ctaString);

        // Handle case where backend returns an array of CTA objects
        if (Array.isArray(parsedCta) && parsedCta.length > 0) {
          this.cta = parsedCta[0]; // Take the first item from the array

          // Additional check to ensure ctaLink is properly mapped
          if ((this.cta as CtaProps).ctaLink && !(this.cta as CtaProps).ctaLinkVal) {
            (this.cta as CtaProps).ctaLinkVal = (this.cta as CtaProps).ctaLink;
          }
        } else {
          this.cta = parsedCta;
        }
      } catch (e) {
        // More aggressive approach to fix JSON with special characters
        try {
          // Remove the problematic :type property entirely
            let fixedCtaString = typeof this.cta === 'string' ? this.cta.replace(/,\s*":type":"[^"]*"(?=\s*\})/g, '') : '';
          const parsedCta = JSON.parse(fixedCtaString);

          if (Array.isArray(parsedCta) && parsedCta.length > 0) {
            this.cta = parsedCta[0];
          } else {
            this.cta = parsedCta;
          }
        } catch (secondError) {
          // Set to empty object after all attempts failed
          this.cta = {};
        }
      }
    } else if (typeof this.cta !== 'object' || this.cta === null) {
      // Initialize as empty object if not a string or object
      this.cta = {};
    }
  }

  componentDidLoad() {
    // Set up intersection observer for animations
    this.setupAnimationObserver();
  }

  disconnectedCallback() {
    // Clean up observer when component is removed
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * Set up the intersection observer for animations
   */
  private setupAnimationObserver() {
    // Use Intersection Observer API instead of jQuery scroll events
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.animateQuoteElements();
          // Once triggered, we can disconnect
          this.observer.disconnect();
        }
      });
    }, { threshold: 0.1 });

    this.observer.observe(this.el);
  }

  /**
   * Animate the quote elements with staggered timing
   */
  private animateQuoteElements() {
    // Use State variables instead of directly manipulating DOM
    setTimeout(() => {
      this.iconActive = true;
    }, 500);

    setTimeout(() => {
      this.quoteActive = true;
    }, 1000);

    setTimeout(() => {
      this.contentActive = true;
    }, 1500);
  }

  render() {
    return (
      <Host>

         <div class={`vs-marquee-content-wrapper ${this.backgroundColor}`}>
          <blockquote class="vs-container vs-content-section vs-temp vs-pt-0 vs-quote-section">
            {this.renderQuoteIcon()}
            {this.renderQuoteText()}
            {this.renderAttribution()}
          </blockquote>
        </div>
        {!this.removeBottomMargin && <div class="vs-quote-bottom-margin" aria-hidden="true"></div>}
      </Host>
    );
  }
}

/**
 * Interface for CTA properties
 */
interface CtaProps {
  ctaStyle?: string;
  ctaButtonColor?: string;
  disableCta?: string;
  ctaTitle?: string;
  ctaLinkVal?: string;
  ctaLinkType?: string;
  ctaLinkTarget?: string;
  ctaLink?: string;  // Add this property to match the backend data
  ctaNoFollow?: string;
  ctaAnchorLabelRequired?: string;
  ctaAnchorLabel?: string;
  ctaLinkAriaLabel?: string;
  ctaConversionType?: string;
  ctaVideoType?: string;
  ctaVideoUrl?: string;
  ctaVideoTitle?: string;
  ctaVideoImagePath?: string;
  ctaVideoAltText?: string;
}
